import type { ComponentType } from 'react'
import AccountingTransactionsShell from 'src/app/AccountingTransactionsShell/AccountingTransactionsShell'
import ExpenseReportAccounting from 'src/app/ExpenseReport/ExpenseReportAccounting'
import ExpenseReportManagerReview from 'src/app/ExpenseReport/ExpenseReportManagerReview'
import ExpensesNeedsApproval from 'src/app/ExpensesNeedsApproval/ExpensesNeedsApproval'
import ManagerApproverExpenseReports from 'src/app/ManagerApproverExpenseReports/ManagerApproverExpenseReports'
import Onboarding from 'src/app/Onboarding/Onboarding'
import OnboardingCard from 'src/app/Onboarding/OnboardingCard'
import OrgInviteDecision from 'src/app/OrgInviteDecision/OrgInviteDecision'
import Transactions from 'src/app/Transactions/Transactions'
import {
  accountsEntityGatePaths,
  accountsFamilyGatePaths,
} from 'src/navigation/accountsRouteTree'
import {
  budgetsEntityGatePaths,
  budgetsFamilyGatePaths,
} from 'src/navigation/budgetsRouteTree'
import type {
  AuthenticatedFlatAppRouteMetadata,
  FlatAppRouteMetadata,
} from 'src/navigation/flatArchitectureRouteMetadata'
import {
  AccountingReviewReportsFlatRouteMetadata,
  ActivityFlatRouteMetadata,
  DownloadsFlatRouteMetadata,
  ExpenseReportFlatRouteMetadata,
  FAQFlatRouteMetadata,
  InsightsFlatRouteMetadata,
  ManagerReviewReimbursementDetailFlatRouteMetadata,
  ManagerReviewReimbursementsFlatRouteMetadata,
  ManagerReviewTransactionsFlatRouteMetadata,
  MarcomPricingFlatRouteMetadata,
  OnboardingCardFlatRouteMetadata,
  OnboardingFlatRouteMetadata,
  OrgInviteDecisionFlatRouteMetadata,
  PaymentsFlatRouteMetadata,
  PricingFlatRouteMetadata,
  PrivacyFlatRouteMetadata,
  StreamingAgentFlatRouteMetadata,
  SupportFlatRouteMetadata,
  TermsFlatRouteMetadata,
  TransactionsFlatRouteMetadata,
  VendorsFlatRouteMetadata,
} from 'src/navigation/flatArchitectureRouteMetadata'
import { organizationGatePaths } from 'src/navigation/organizationRouteTree'
import { profileFamilyGatePaths } from 'src/navigation/profileRouteTree'
import AccountEntityPage from 'src/page-components/Accounts/AccountEntityPage/AccountEntityPage'
import AccountsFamilyPage from 'src/page-components/Accounts/AccountsFamilyPage/AccountsFamilyPage'
import Activity from 'src/page-components/Activity/Activity'
import BudgetEntityPage from 'src/page-components/Budgets/BudgetEntityPage/BudgetEntityPage'
import BudgetsFamilyPage from 'src/page-components/Budgets/BudgetsFamilyPage/BudgetsFamilyPage'
import Downloads from 'src/page-components/Downloads/Downloads'
import FAQ from 'src/page-components/FAQ/FAQ'
import InsightsPage from 'src/page-components/InsightsPage/InsightsPage'
import MarcomPricingPage from 'src/page-components/MarcomPricingPage/MarcomPricingPage'
import OrganizationSettingsPage from 'src/page-components/Organization/OrganizationSettingsPage/OrganizationSettingsPage'
import PaymentsPage from 'src/page-components/Payments/PaymentsPage'
import PricingPage from 'src/page-components/PricingPage/PricingPage'
import Privacy from 'src/page-components/Privacy/Privacy'
import ProfileFamilyPage from 'src/page-components/Profile/ProfileFamilyPage/ProfileFamilyPage'
import StreamingAgentPage from 'src/page-components/StreamingAgentPage/StreamingAgentPage'
import Support from 'src/page-components/Support/Support'
import Terms from 'src/page-components/Terms/Terms'
import VendorsPage from 'src/page-components/Vendors/VendorsPage'

export type FlatAppRoute = Omit<FlatAppRouteMetadata, 'path'> & {
  // A tabbed family registers ONE gate entry whose `path` is an array covering
  // every URL in that family. Matching any of them resolves the same entry, so
  // the page (and its persistent Subnav) stays mounted across tabs — see the
  // gate in App.tsx and src/component-layout/FamilyLayout. Single-page routes
  // keep using a plain string.
  path: string | string[]
  Component: ComponentType
}

export type AuthenticatedFlatAppRoute = Omit<
  AuthenticatedFlatAppRouteMetadata,
  'path'
> & {
  // See `FlatAppRoute.path` — tabbed families register one entry with an array
  // of paths.
  path: string | string[]
  Component: ComponentType
}

// Authed flat pages. These render in `Authed.tsx`'s one page <Switch>, as a
// block BEFORE the not-yet-migrated legacy routes — precedence is source order,
// no ranker. PARAM (`:id`) routes have an ordering hazard: a flat `:id` here is
// greedy and would shadow a same-depth static sibling still in the legacy
// registry. Before adding a param route, confirm its static siblings are flat
// too (or co-migrate them), and order specific-before-param. See
// `src/navigation/AGENTS.md` → "Flat route invariants".
export const AuthenticatedFlatArchitectureRoutes: AuthenticatedFlatAppRoute[] =
  [
    {
      ...PricingFlatRouteMetadata,
      Component: PricingPage,
    },
    {
      ...DownloadsFlatRouteMetadata,
      Component: Downloads,
    },
    {
      ...TransactionsFlatRouteMetadata,
      Component: Transactions,
    },
    {
      ...ActivityFlatRouteMetadata,
      Component: Activity,
    },
    {
      ...VendorsFlatRouteMetadata,
      Component: VendorsPage,
    },
    {
      ...PaymentsFlatRouteMetadata,
      Component: PaymentsPage,
    },
    {
      ...InsightsFlatRouteMetadata,
      Component: InsightsPage,
    },
    {
      ...StreamingAgentFlatRouteMetadata,
      Component: StreamingAgentPage,
    },
    {
      ...AccountingReviewReportsFlatRouteMetadata,
      Component: AccountingTransactionsShell,
    },
    {
      ...OnboardingFlatRouteMetadata,
      Component: Onboarding,
    },
    {
      ...OnboardingCardFlatRouteMetadata,
      Component: OnboardingCard,
    },
    {
      ...OrgInviteDecisionFlatRouteMetadata,
      Component: OrgInviteDecision,
    },
    {
      ...ManagerReviewTransactionsFlatRouteMetadata,
      Component: ExpensesNeedsApproval,
    },
    {
      ...ManagerReviewReimbursementsFlatRouteMetadata,
      Component: ManagerApproverExpenseReports,
    },
    {
      ...ExpenseReportFlatRouteMetadata,
      Component: ExpenseReportAccounting,
    },
    {
      ...ManagerReviewReimbursementDetailFlatRouteMetadata,
      Component: ExpenseReportManagerReview,
    },
    // EX-39748 — Budgets tabbed family. ONE gate entry per family; each `path`
    // array covers every URL in that family so the page + its FamilyLayout Subnav
    // stay mounted across tab navigation (preserving the sliding active-bar). The
    // page owns the nested tab routing. Paths derive from budgetsRouteTree.
    {
      path: budgetsFamilyGatePaths,
      Component: BudgetsFamilyPage,
      renderInAuthedShell: true,
    },
    {
      path: budgetsEntityGatePaths,
      Component: BudgetEntityPage,
      renderInAuthedShell: true,
    },
    // EX-39923 — Accounts tabbed family (supersedes #6986). ONE gate entry per
    // family; each `path` array covers every URL in that family so the page stays
    // mounted across navigation. The FAMILY entry MUST precede the ENTITY entry so
    // static family paths (`/accounts/youraccounts`) beat the entity `/accounts/:id`
    // param at equal depth (source order in the one-switch). Paths derive from
    // accountsRouteTree. NB: the Accounts family is NOT tabbed — AccountsFamilyPage
    // preserves the legacy "Physical Cards" no-tab-bar shell.
    {
      path: accountsFamilyGatePaths,
      Component: AccountsFamilyPage,
      renderInAuthedShell: true,
    },
    {
      path: accountsEntityGatePaths,
      Component: AccountEntityPage,
      renderInAuthedShell: true,
    },
    // EX-39748 — Profile ("Profile Settings") family. ONE multi-path gate entry
    // covers every static /profile URL so the page stays mounted across sub-page
    // navigation. Static-only family (no `/:id` entity), so no entity entry and
    // no param-child matcher. The page owns the nested content routing and
    // preserves the legacy no-tab-bar "Profile Settings" shell. Profile overlays
    // stay in the legacy overlay registry. Paths derive from profileRouteTree.
    {
      path: profileFamilyGatePaths,
      Component: ProfileFamilyPage,
      renderInAuthedShell: true,
    },
    // EX-39748 — Organization-settings family. ONE gate entry whose `path` array
    // (organizationGatePaths) covers the whole `/organizationSettings/:id` subtree
    // plus the `/cardfeeds*` routes, so OrganizationSettingsPage stays mounted and
    // owns the nested settings routing. Unlike Budgets/Accounts there is no
    // family/entity split — the org IS the entity — so it registers as a single
    // entry. Card-feed static `/cardfeeds/create` precedes `/cardfeeds/:id` in the
    // array so the static path beats the param at equal depth.
    {
      path: organizationGatePaths,
      Component: OrganizationSettingsPage,
      renderInAuthedShell: true,
    },
    // {
    //   path: Routes.CANCEL_SUBSCRIPTION_ROUTE(),
    //   Component: CancelSubscriptionPage,
    // },
    /* HYGEN-INJECT-ROUTE */
  ]

export const AuthenticationAgnosticFlatArchitectureRoutes: FlatAppRoute[] = [
  {
    ...FAQFlatRouteMetadata,
    Component: FAQ,
  },
  {
    ...TermsFlatRouteMetadata,
    Component: Terms,
  },
  {
    ...PrivacyFlatRouteMetadata,
    Component: Privacy,
  },
  {
    ...SupportFlatRouteMetadata,
    Component: Support,
  },
]

export const PublicFlatArchitectureRoutes: FlatAppRoute[] = [
  {
    ...MarcomPricingFlatRouteMetadata,
    Component: MarcomPricingPage,
  },
]
